<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-container v-if="!loadingPersonal">
          <v-row align="center" class="mb-4" v-if="personalCompleto && !hidePersonalInfo">
            <v-col cols="12" md="2" class="d-flex justify-center">
              <v-avatar color="secondary" rounded size="80" v-if="!personalCompleto.foto">
                <v-icon size="64" color="white">mdi-account</v-icon>
              </v-avatar>
              <v-img
                v-else
                :src="personalCompleto.foto"
                max-height="80"
                max-width="80"
                class="rounded"
                @error="personalCompleto.foto = null"
              ></v-img>
            </v-col>
            <v-col cols="12" md="10">
              <div class="text-h6 font-weight-bold text--primary">
                {{ getFullName(personalCompleto) }}
              </div>
              <div class="text-subtitle-2 text--secondary mt-1">CUIP: {{ personalCompleto.CUIP || 'Sin CUIP' }}</div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-card class="ui-module-card ui-module-fade-in-up asistencias-shell" flat :loading="loading">
                <v-card-title class="ui-module-titlebar d-flex align-center justify-space-between flex-wrap">
                  <span class="text-h6 font-weight-bold">Historial de Asistencias</span>
                  <div class="d-flex align-center flex-wrap" style="gap: 8px" v-if="idSubsede">
                    <v-menu
                      v-model="menuFechaIni"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <BaseInput
                          :value="fechaIniDisplay"
                          label="Desde"
                          readonly
                          style="max-width: 160px"
                          v-bind="attrs"
                          v-on="on"
                        />
                      </template>
                      <v-date-picker
                        v-model="fechaIni"
                        :max="fechaFin"
                        locale="es"
                        no-title
                        @input="onFechaRangoChange('ini')"
                      />
                    </v-menu>
                    <v-menu
                      v-model="menuFechaFin"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <BaseInput
                          :value="fechaFinDisplay"
                          label="Hasta"
                          readonly
                          style="max-width: 160px"
                          v-bind="attrs"
                          v-on="on"
                        />
                      </template>
                      <v-date-picker
                        v-model="fechaFin"
                        :min="fechaIni"
                        :max="hoyVuetify"
                        locale="es"
                        no-title
                        @input="onFechaRangoChange('fin')"
                      />
                    </v-menu>
                  </div>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">
                        <v-btn
                          icon
                          color="secondary"
                          class="ui-module-icon-action"
                          :disabled="loading || !asistencias || asistencias.length === 0"
                          @click="downloadAsistenciasCsv"
                        >
                          <v-icon>mdi-download</v-icon>
                        </v-btn>
                      </span>
                    </template>
                    <span>Descargar CSV</span>
                  </v-tooltip>
                </v-card-title>
                <v-card-text>
                  <v-data-table
                    :headers="headers"
                    :items="asistencias"
                    class="transparent rounded-lg"
                    :loading="loading"
                    loading-text="Cargando asistencias..."
                    :items-per-page="10"
                    :footer-props="{
                      'items-per-page-options': [5, 10, 15, 20, -1],
                      'items-per-page-text': 'Registros por página:',
                      'show-current-page': true,
                      'show-first-last-page': true,
                    }"
                    calculate-widths
                  >
                    <template v-slot:[`item.fechaHora`]="{ item }">
                      {{ formatDateTime(item.fecha) }}
                    </template>
                    <template v-slot:[`item.tipoAsistencia`]="{ item }">
                      <v-chip small :color="getTipoAsistenciaColor(item.fk_idTipoAsistencia)" text-color="white">
                        {{ getTipoAsistenciaTitle(item.fk_idTipoAsistencia) }}
                      </v-chip>
                    </template>
                    <template v-slot:[`item.servicio`]="{ item }">
                      {{ getTipoServicioTitle(item) }}
                    </template>
                    <template v-slot:[`item.crpUnidad`]="{ item }">
                      {{ getCrpUnidad(item) }}
                    </template>
                    <template v-slot:[`item.radio`]="{ item }">
                      {{ item.radio || '-' }}
                    </template>
                    <template v-slot:[`item.sector`]="{ item }">
                      {{ item.sector || '-' }}
                    </template>
                    <template v-slot:[`item.salida`]="{ item }">
                      {{ formatDateTime(item.salida) }}
                    </template>
                    <template v-slot:[`item.estatus_previo`]="{ item }">
                      <v-chip
                        v-if="item.estatus_previo"
                        small
                        :color="getTipoAsistenciaColor(item.estatus_previo)"
                        text-color="white"
                      >
                        {{ getTipoAsistenciaTitle(item.estatus_previo) }}
                      </v-chip>
                      <span v-else>—</span>
                    </template>
                    <template v-slot:[`item.fecha_previa`]="{ item }">
                      {{ item.fecha_previa ? formatDateTime(item.fecha_previa) : '—' }}
                    </template>
                    <template v-slot:[`item.acciones`]="{ item }">
                      <div class="d-flex align-center justify-center">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on">
                              <v-btn
                                icon
                                small
                                color="secondary"
                                class="ui-module-icon-action ui-module-btn-solid"
                                :disabled="!!item.salida || loadingSalida === item.idAsistencia"
                                :loading="loadingSalida === item.idAsistencia"
                                @click="abrirConfirmacionSalida(item)"
                              >
                                <v-icon small>mdi-location-exit</v-icon>
                              </v-btn>
                            </span>
                          </template>
                          <span>{{ item.salida ? 'Salida ya registrada' : 'Registrar salida' }}</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on">
                              <v-btn
                                icon
                                small
                                color="warning"
                                class="ui-module-icon-action"
                                :disabled="
                                  !!(item.fecha_previa || item.estatus_previo) ||
                                  (loadingEditar && itemParaEditar && itemParaEditar.idAsistencia === item.idAsistencia)
                                "
                                :loading="
                                  loadingEditar && itemParaEditar && itemParaEditar.idAsistencia === item.idAsistencia
                                "
                                @click="abrirEditar(item)"
                              >
                                <v-icon small>mdi-pencil</v-icon>
                              </v-btn>
                            </span>
                          </template>
                          <span>{{
                            item.fecha_previa || item.estatus_previo
                              ? 'Ya fue editada anteriormente'
                              : 'Editar asistencia'
                          }}</span>
                        </v-tooltip>
                      </div>
                    </template>
                  </v-data-table>
                  <div v-if="!loading && (!asistencias || asistencias.length === 0)" class="text-center py-8">
                    <v-icon large color="secondary">mdi-clipboard-text-off-outline</v-icon>
                    <div class="text-subtitle-1 text--primary mt-2">
                      El personal seleccionado no tiene asistencias registradas.
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
        <!-- Loader mientras carga el personal -->
        <v-container v-else>
          <v-row align="center" justify="center" class="py-12">
            <v-col cols="12" align="center">
              <v-progress-circular indeterminate color="secondary" size="64"></v-progress-circular>
              <div class="text-subtitle-1 text--primary mt-4">Cargando información del personal...</div>
            </v-col>
          </v-row>
        </v-container>
      </v-col>
    </v-row>

    <v-dialog v-model="confirmacionSalidaDialog" max-width="400" persistent>
      <v-card>
        <v-card-title class="text-h6">Confirmar registro de salida</v-card-title>
        <v-card-text>
          ¿Está seguro de registrar la salida para esta asistencia? Esta acción no se puede deshacer.
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn text :disabled="loadingSalida !== null" @click="cancelarConfirmacionSalida">Cancelar</v-btn>
          <v-btn
            color="secondary"
            class="ui-module-btn-solid"
            :loading="loadingSalida !== null"
            :disabled="loadingSalida !== null"
            @click="confirmarSalida"
          >
            Sí, registrar salida
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="editDialog" max-width="520" persistent>
      <v-card>
        <v-card-title class="text-h6">Editar registro de asistencia</v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <BaseSelect
                  v-model="editForm.fk_idTipoAsistencia"
                  :items="tiposAsistencia"
                  label="Tipo de asistencia"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-menu
                  v-model="menuFechaEditar"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <BaseInput :value="editFechaDisplay" label="Fecha" readonly v-bind="attrs" v-on="on" />
                  </template>
                  <v-date-picker v-model="editForm.fecha" locale="es" no-title @input="menuFechaEditar = false" />
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6">
                <BaseInput
                  v-model="editForm.hora"
                  label="Hora"
                  v-mask="'##:##'"
                  placeholder="HH:MM"
                  :error-messages="horaError"
                  @blur="validarHora"
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="editForm.observaciones"
                  label="Observaciones *"
                  rounded
                  filled
                  dense
                  no-resize
                  rows="3"
                  color="secondary"
                  background-color="primary"
                  hint="Motivo de la corrección (obligatorio)"
                  persistent-hint
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn text :disabled="loadingEditar" @click="cancelarEditar">Cancelar</v-btn>
          <v-btn
            color="warning"
            :loading="loadingEditar"
            :disabled="loadingEditar || !editForm.observaciones || !editForm.fk_idTipoAsistencia || !editForm.fecha"
            @click="confirmarEditar"
          >
            Guardar corrección
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from 'moment'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import downloadCsv from '@/utils/downloadCsv'
import BaseSelect from '@/components/base/BaseSelect.vue'
import BaseInput from '@/components/base/BaseInput.vue'

export default {
  name: 'ListadoAsistenciasModal',

  components: { BaseSelect, BaseInput },

  props: {
    selectedPersonal: {
      type: Object,
      required: true,
    },
    hidePersonalInfo: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      headers: [
        { text: 'Fecha y hora', value: 'fechaHora', align: 'center' },
        { text: 'Tipo de Asistencia', value: 'tipoAsistencia' },
        { text: 'Servicio', value: 'servicio' },
        { text: 'CRP / Unidad', value: 'crpUnidad' },
        { text: 'Radio', value: 'radio' },
        { text: 'Sector', value: 'sector' },
        { text: 'Salida', value: 'salida', align: 'center' },
        { text: 'Tipo previo', value: 'estatus_previo', align: 'center', sortable: false },
        { text: 'Fecha previo', value: 'fecha_previa', align: 'center', sortable: false },
        { text: 'Acciones', value: 'acciones', align: 'center', sortable: false },
      ],
      loadingSalida: null,
      confirmacionSalidaDialog: false,
      itemParaSalida: null,
      loading: false,
      loadingPersonal: false,
      personalCompleto: null,
      DISPLAY_DATE_FORMAT: 'DD/MM/YYYY',
      // Edit dialog
      editDialog: false,
      itemParaEditar: null,
      loadingEditar: false,
      editForm: {
        fk_idTipoAsistencia: null,
        fecha: '',
        hora: '',
        observaciones: '',
      },
      horaError: '',
      menuFechaEditar: false,
      fechaIni: moment().subtract(1, 'month').format('YYYY-MM-DD'),
      fechaFin: moment().format('YYYY-MM-DD'),
      menuFechaIni: false,
      menuFechaFin: false,
    }
  },

  computed: {
    ...mapGetters('asistencia', {
      asistenciasStore: 'getAsistencias',
      tiposAsistencia: 'getTiposAsistencia',
      tiposServicio: 'getTiposServicio',
    }),

    asistencias() {
      if (!this.idSubsede) return this.asistenciasStore

      const idPersonal = this.selectedPersonal.idPersonal || this.selectedPersonal.id_personal
      return (this.asistenciasStore || []).filter((item) => Number(item.fk_idPersonal) === Number(idPersonal))
    },

    editFechaDisplay() {
      if (!this.editForm.fecha) return ''
      return moment(this.editForm.fecha, 'YYYY-MM-DD').format('DD/MM/YYYY')
    },

    idSubsede() {
      return this.selectedPersonal?.fk_idSubsede || this.personalCompleto?.fk_idSubsede || null
    },

    hoyVuetify() {
      return moment().format('YYYY-MM-DD')
    },

    fechaIniDisplay() {
      return this.fechaIni ? moment(this.fechaIni, 'YYYY-MM-DD').format('DD/MM/YYYY') : ''
    },

    fechaFinDisplay() {
      return this.fechaFin ? moment(this.fechaFin, 'YYYY-MM-DD').format('DD/MM/YYYY') : ''
    },
  },

  watch: {
    'editForm.hora'(val) {
      if (!val) this.horaError = ''
    },
  },

  methods: {
    ...mapActions('asistencia', [
      'fetchAsistencias',
      'fetchAsistenciasBySubsede',
      'fetchTipoAsistencia',
      'fetchTipoServicio',
      'patchSalida',
      'updateAsistencia',
    ]),
    ...mapActions('personal', ['fetchPersonalById']),
    ...mapMutations(['SET_SNACKBAR_MESSAGE']),

    formatDate(date) {
      if (!date) return 'Sin fecha'

      let cleanDate = date

      if (typeof date === 'string' && date.includes(' ')) {
        cleanDate = date.split(' ')[0]
      }
      let momentDate = moment(cleanDate, 'DD/MM/YYYY', true)

      if (!momentDate.isValid()) {
        momentDate = moment(cleanDate, 'YYYY/MM/DD', true)
      }

      if (!momentDate.isValid()) {
        console.warn('Fecha inválida recibida:', date)
        return 'Fecha inválida'
      }

      return momentDate.format(this.DISPLAY_DATE_FORMAT)
    },

    getHora(item) {
      if (item?.hora) return item.hora

      if (typeof item?.fecha === 'string' && item.fecha.includes(' ')) {
        const partes = item.fecha.split(' ')
        return partes[1] || '-'
      }

      return '-'
    },

    getTipoAsistenciaTitle(idTipoAsistencia) {
      if (!this.tiposAsistencia || this.tiposAsistencia.length === 0) {
        return 'Cargando...'
      }

      const tipo = this.tiposAsistencia.find((t) => t.id === idTipoAsistencia)
      return tipo ? tipo.title : 'Desconocido'
    },

    getTipoAsistenciaColor(idTipoAsistencia) {
      const colorMap = {
        1: 'success', // Asistencia
        2: 'warning', // Retardo
        3: 'error', // Falta
        4: 'info', // Justificada
      }
      return colorMap[idTipoAsistencia] || 'secondary'
    },

    getTipoServicioTitle(item) {
      const idTipoServicio = item?.fk_idTipoServicio
      if (!idTipoServicio) return '-'
      if (!this.tiposServicio || this.tiposServicio.length === 0) return '-'

      const tipo = this.tiposServicio.find((t) => t.id === idTipoServicio)
      return tipo?.title || '-'
    },

    getCrpUnidad(item) {
      return item?.crp || '-'
    },

    getFullName(person) {
      if (!person) return 'Sin nombre'

      const nombre = person.nombre || ''
      const segundoNombre = person.segundoNombre || ''
      const apaterno = person.apaterno || ''
      const amaterno = person.amaterno || ''

      return `${apaterno} ${amaterno} ${nombre} ${segundoNombre}`.trim()
    },

    async fetchPersonalCompleto() {
      if (!this.selectedPersonal) return

      try {
        this.loadingPersonal = true
        const idPersonal = this.selectedPersonal.idPersonal || this.selectedPersonal.id_personal

        await this.fetchPersonalById(idPersonal)

        this.personalCompleto = this.$store.getters['personal/getPersonalSingle']
      } catch (error) {
        this.SET_SNACKBAR_MESSAGE('Error al cargar la información del personal.')
        this.personalCompleto = null
      } finally {
        this.loadingPersonal = false
      }
    },

    async loadData() {
      try {
        this.loading = true

        await Promise.all([this.fetchTipoAsistencia(), this.fetchTipoServicio(), this.fetchPersonalCompleto()])

        const personalId = this.selectedPersonal.idPersonal || this.selectedPersonal.id_personal

        if (this.idSubsede) {
          await this.fetchAsistenciasBySubsede({
            idSubSede: this.idSubsede,
            fechaIni: this.fechaIni,
            fechaFin: this.fechaFin,
            idPersonal: personalId,
          })
        } else {
          await this.fetchAsistencias(personalId)
        }
      } catch (error) {
        this.SET_SNACKBAR_MESSAGE('Error al cargar las asistencias.')
      } finally {
        this.loading = false
      }
    },

    onFechaRangoChange(which) {
      if (which === 'ini') this.menuFechaIni = false
      else this.menuFechaFin = false
      this.loadData()
    },

    formatDateTime(datetime) {
      if (!datetime) return '—'
      const momentDate = moment(datetime, 'DD/MM/YYYY hh:mm:ss A', 'es')
      if (!momentDate.isValid()) return datetime
      return momentDate.format('DD/MM/YYYY hh:mm A')
    },

    abrirConfirmacionSalida(item) {
      this.itemParaSalida = item
      this.confirmacionSalidaDialog = true
    },

    cancelarConfirmacionSalida() {
      this.confirmacionSalidaDialog = false
      this.itemParaSalida = null
    },

    async confirmarSalida() {
      if (!this.itemParaSalida) return
      const item = this.itemParaSalida
      this.confirmacionSalidaDialog = false
      this.itemParaSalida = null
      this.loadingSalida = item.idAsistencia
      try {
        await this.patchSalida({ idPersonal: item.fk_idPersonal, idAsistencia: item.idAsistencia })
      } finally {
        this.loadingSalida = null
      }
    },

    abrirEditar(item) {
      this.itemParaEditar = item
      const formatos = [
        'DD/MM/YYYY hh:mm:ss A',
        'DD/MM/YYYY HH:mm:ss',
        'DD/MM/YYYY HH:mm',
        'DD/MM/YYYY',
        'YYYY/MM/DD HH:mm',
        'YYYY/MM/DD',
      ]
      const fechaMoment = moment(item.fecha, formatos)
      this.editForm = {
        fk_idTipoAsistencia: item.fk_idTipoAsistencia,
        fecha: fechaMoment.isValid() ? fechaMoment.format('YYYY-MM-DD') : '',
        hora: fechaMoment.isValid() ? fechaMoment.format('HH:mm') : this.getHora(item) || '',
        observaciones: '',
      }
      this.editDialog = true
    },

    cancelarEditar() {
      this.editDialog = false
      this.itemParaEditar = null
      this.editForm = { fk_idTipoAsistencia: null, fecha: '', hora: '', observaciones: '' }
      this.horaError = ''
    },

    validarHora() {
      if (!this.editForm.hora || this.editForm.hora.length < 5) {
        this.horaError = ''
        return
      }
      const [hh, mm] = this.editForm.hora.split(':').map(Number)
      if (hh > 23 || mm > 59) {
        this.horaError = 'Hora inválida (00:00 – 23:59)'
      } else {
        this.horaError = ''
      }
    },

    async confirmarEditar() {
      if (!this.itemParaEditar) return
      if (!this.editForm.observaciones?.trim()) {
        this.SET_SNACKBAR_MESSAGE('Las observaciones son obligatorias.')
        return
      }
      if (this.editForm.hora && !moment(this.editForm.hora, 'HH:mm', true).isValid()) {
        this.SET_SNACKBAR_MESSAGE('Ingresa una hora válida con formato HH:mm.')
        return
      }
      const item = this.itemParaEditar
      this.loadingEditar = true
      try {
        const fechaApi =
          moment(this.editForm.fecha, 'YYYY-MM-DD').format('YYYY/MM/DD') + ' ' + (this.editForm.hora || '00:00')
        const payload = {
          id: item.idAsistencia,
          fk_idPersonal: item.fk_idPersonal,
          fk_idTipoAsistencia: this.editForm.fk_idTipoAsistencia,
          fk_idTipoServio: item.fk_idTipoServio || null,
          crp: item.crp || null,
          radio: item.radio || null,
          sector: item.sector || null,
          fecha: fechaApi,
          observaciones: this.editForm.observaciones.trim(),
        }
        await this.updateAsistencia({ idPersonal: item.fk_idPersonal, idAsistencia: item.idAsistencia, payload })
        this.cancelarEditar()
      } catch (_) {
        // snackbar set by store
      } finally {
        this.loadingEditar = false
      }
    },

    downloadAsistenciasCsv() {
      const rows = (this.asistencias || []).map((item) => ({
        'Fecha y hora': this.formatDateTime(item.fecha),
        'Tipo de Asistencia': this.getTipoAsistenciaTitle(item.fk_idTipoAsistencia),
        Servicio: this.getTipoServicioTitle(item),
        'CRP / Unidad': this.getCrpUnidad(item),
        Radio: item.radio || '-',
        Sector: item.sector || '-',
      }))

      const fullName = this.getFullName(this.personalCompleto)
      const safeName = (fullName || 'personal')
        .replace(/[\\/:*?"<>|]+/g, '')
        .replace(/\s+/g, '_')
        .toLowerCase()
      downloadCsv(rows, { filename: `historial-asistencias-${safeName}` })
    },
  },

  async mounted() {
    moment.locale('es')
    await this.loadData()
  },
}
</script>

<style scoped>
.rounded {
  border-radius: 8px;
}

.asistencias-shell {
  border-radius: 16px;
}
</style>
